import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

// Wave Animation Component
const WaveAnimation = () => {
  return (
    <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden">
      <motion.div
        className="relative h-[300px]"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        {/* Glow effect at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#72288D]/20 to-transparent" />

        {/* First wave - primary purple */}
        <motion.div
          className="absolute bottom-[-10%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.25' fill='%2372288D'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [0, -500],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Second wave - secondary purple */}
        <motion.div
          className="absolute bottom-[-20%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.2' fill='%236720A3'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [-500, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Third wave - tertiary purple */}
        <motion.div
          className="absolute bottom-[-30%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.3' fill='%236D2398'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [0, -600],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Fourth wave - accent purple */}
        <motion.div
          className="absolute bottom-[-40%] h-[300px] w-[200%]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' opacity='.15' fill='%23843866'%3E%3C/path%3E%3C/svg%3E\")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            x: [-600, 0],
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Animated purple dust particles on waves */}
        <div className="absolute inset-0">
          {Array.from({ length: 30 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-1 w-1 rounded-full bg-[#72288D]"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${50 + Math.random() * 50}%`,
                boxShadow: "0 0 5px 1px rgba(114, 40, 141, 0.7)",
              }}
              animate={{
                opacity: [0, 1, 0],
                y: [0, -20],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// Define particle types with purple color variations
const particleTypes = [
  { icon: "♪", size: "text-sm", color: "text-[#72288D]/60" },
  { icon: "♫", size: "text-lg", color: "text-[#6720A3]/60" },
  { icon: "♩", size: "text-base", color: "text-[#6D2398]/50" },
  { icon: "♬", size: "text-xl", color: "text-[#72288D]/40" },
  { icon: "✦", size: "text-xs", color: "text-[#843866]/70" },
  { icon: "✧", size: "text-xs", color: "text-[#4A4A4A]/50" },
  { icon: "✶", size: "text-sm", color: "text-[#6720A3]/50" },
  { icon: "⋆", size: "text-xs", color: "text-[#6D2398]/60" },
  { icon: "⊹", size: "text-base", color: "text-[#4A4A4A]/40" },
  { icon: "★", size: "text-sm", color: "text-[#72288D]/60" },
  { icon: "✵", size: "text-xs", color: "text-[#843866]/70" },
];

// Particle Background Component
const ParticleBackground = () => {
  // Memoize the particles array so it doesn't change on re-renders
  const particles = useMemo(
    () =>
      Array.from({ length: 80 }).map((_, i) => {
        const type =
          particleTypes[Math.floor(Math.random() * particleTypes.length)];
        return {
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 5,
          duration: 15 + Math.random() * 30,
          type,
        };
      }),
    [],
  ); // Empty dependency array means this will only run once

  // Memoize the flare particles
  const flareParticles = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, i) => {
        const colors = ["#72288D", "#6720A3", "#6D2398", "#843866"];
        const color = colors[i % colors.length];
        return {
          id: i,
          x: 10 + Math.random() * 80,
          y: 10 + Math.random() * 80,
          size: 3 + Math.random() * 6,
          color,
        };
      }),
    [],
  ); // Empty dependency array means this will only run once

  // Memoize the lens flares
  const lensFlares = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => {
        const colors = ["#72288D", "#6720A3", "#6D2398"];
        const color = colors[i % colors.length];
        return {
          id: i,
          x: 20 + Math.random() * 60,
          y: 20 + Math.random() * 60,
          width: 100 + Math.random() * 200,
          height: 10 + Math.random() * 20,
          rotation: Math.random() * 180,
          color,
        };
      }),
    [],
  ); // Empty dependency array means this will only run once

  return (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {/* Purple dust particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className={`absolute ${particle.type.size} ${particle.type.color} pointer-events-none select-none`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            textShadow: "0 0 8px rgba(114,40,141,0.6)",
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0, 1, 0],
            scale: [0.8, 1, 0.8],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {particle.type.icon}
        </motion.div>
      ))}

      {/* Premium purple flares */}
      {flareParticles.map((flare) => (
        <motion.div
          key={`flare-${flare.id}`}
          className="absolute rounded-full"
          style={{
            left: `${flare.x}%`,
            top: `${flare.y}%`,
            width: `${flare.size}px`,
            height: `${flare.size}px`,
            background: `radial-gradient(circle, ${flare.color} 0%, transparent 70%)`,
            boxShadow: `0 0 10px 5px ${flare.color}30`,
          }}
          animate={{
            opacity: [0, 0.8, 0],
            scale: [0.5, 1.5, 0.5],
          }}
          transition={{
            duration: 3 + Math.random() * 4,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Luxury lens flares */}
      {lensFlares.map((lens) => (
        <motion.div
          key={`lens-${lens.id}`}
          className="absolute"
          style={{
            left: `${lens.x}%`,
            top: `${lens.y}%`,
            width: `${lens.width}px`,
            height: `${lens.height}px`,
            background: `linear-gradient(90deg, transparent 0%, ${lens.color}30 50%, transparent 100%)`,
            transform: `rotate(${lens.rotation}deg)`,
          }}
          animate={{
            opacity: [0, 0.7, 0],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 4 + Math.random() * 4,
            repeat: Infinity,
            delay: Math.random() * 10,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Premium Background Component
const PremiumBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const setCanvasDimensions = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasDimensions();
    window.addEventListener("resize", setCanvasDimensions);

    // Create gradient background
    const createGradient = () => {
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#FFFFFF"); // White at top
      gradient.addColorStop(0.3, "#F8F4FB"); // Very light purple
      gradient.addColorStop(0.7, "#F4F0F8"); // Light purple
      gradient.addColorStop(1, "#F0EBF5"); // Slightly more purple at bottom
      return gradient;
    };

    // Draw spotlight effects
    const drawSpotlights = (time) => {
      // Main spotlight
      const centerX = canvas.width / 2;
      const centerY = canvas.height * 0.3;
      const radius = canvas.width * 0.4;

      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        radius,
      );
      gradient.addColorStop(0, "rgba(114, 40, 141, 0.08)"); // Purple at center
      gradient.addColorStop(0.5, "rgba(103, 32, 163, 0.05)"); // Darker purple
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)"); // Transparent at edges

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();

      // Secondary spotlights
      const numSpots = 3;
      for (let i = 0; i < numSpots; i++) {
        const angle = time / 2000 + (i * Math.PI * 2) / numSpots;
        const distance = canvas.width * 0.3;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance * 0.5;
        const spotRadius = canvas.width * 0.15;

        const spotGradient = ctx.createRadialGradient(
          x,
          y,
          0,
          x,
          y,
          spotRadius,
        );

        if (i % 3 === 0) {
          spotGradient.addColorStop(0, "rgba(114, 40, 141, 0.05)");
          spotGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        } else if (i % 3 === 1) {
          spotGradient.addColorStop(0, "rgba(109, 35, 152, 0.04)");
          spotGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        } else {
          spotGradient.addColorStop(0, "rgba(132, 56, 102, 0.03)");
          spotGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        }

        ctx.fillStyle = spotGradient;
        ctx.beginPath();
        ctx.arc(x, y, spotRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    // Draw light beams
    const drawLightBeams = (time) => {
      const numBeams = 5;
      const beamWidth = canvas.width * 0.05;
      const beamLength = canvas.width * 1.5;

      for (let i = 0; i < numBeams; i++) {
        const angle =
          time / 5000 + (i * Math.PI) / (numBeams - 1) - Math.PI / 2;
        const centerX = canvas.width / 2;
        const centerY = canvas.height * 1.1;

        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(angle);

        const beamGradient = ctx.createLinearGradient(0, 0, 0, -beamLength);

        if (i % 3 === 0) {
          beamGradient.addColorStop(0, "rgba(114, 40, 141, 0.08)");
          beamGradient.addColorStop(1, "rgba(114, 40, 141, 0)");
        } else if (i % 3 === 1) {
          beamGradient.addColorStop(0, "rgba(103, 32, 163, 0.06)");
          beamGradient.addColorStop(1, "rgba(103, 32, 163, 0)");
        } else {
          beamGradient.addColorStop(0, "rgba(109, 35, 152, 0.05)");
          beamGradient.addColorStop(1, "rgba(109, 35, 152, 0)");
        }

        ctx.fillStyle = beamGradient;
        ctx.beginPath();
        ctx.rect(-beamWidth / 2, 0, beamWidth, -beamLength);
        ctx.fill();

        ctx.restore();
      }
    };

    // Draw subtle grid
    const drawGrid = (time) => {
      const gridSize = 50;
      const gridOpacity = 0.05;

      ctx.strokeStyle = `rgba(114, 40, 141, ${gridOpacity})`;
      ctx.lineWidth = 0.5;

      // Horizontal lines
      for (let y = 0; y < canvas.height; y += gridSize) {
        const waveOffset = Math.sin(y / 200 + time / 2000) * 5;

        ctx.beginPath();
        ctx.moveTo(0, y + waveOffset);

        for (let x = 0; x < canvas.width; x += 10) {
          const waveY = y + waveOffset + Math.sin(x / 200 + time / 3000) * 3;
          ctx.lineTo(x, waveY);
        }

        ctx.stroke();
      }

      // Vertical lines with less frequency
      for (let x = 0; x < canvas.width; x += gridSize * 2) {
        ctx.beginPath();
        ctx.moveTo(x, 0);

        for (let y = 0; y < canvas.height; y += 10) {
          const waveX = x + Math.sin(y / 200 + time / 2000) * 3;
          ctx.lineTo(waveX, y);
        }

        ctx.stroke();
      }
    };

    // Animation loop
    let animationId;
    const animate = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw base gradient
      ctx.fillStyle = createGradient();
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw grid
      drawGrid(time);

      // Draw light beams
      drawLightBeams(time);

      // Draw spotlights
      drawSpotlights(time);

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", setCanvasDimensions);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />
      {/* Additional overlay effects */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-white/30 to-transparent" />
      {/* Vignette effect */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.4)_100%)]" />
    </>
  );
};

export default function SignupForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    profession: "DJ",
    stageName: "",
    email: "",
    gender: "",
    dob: { day: "", month: "", year: "" },
    location: "",
  });

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i,
  );

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleDOBChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, dob: { ...p.dob, [name]: value } }));
  };
  const [avatarUrl, setAvatarUrl] = useState("");
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File type validation
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPG, PNG)");
      return;
    }

    // File size validation (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setProfileImage(URL.createObjectURL(file));

    const token = localStorage.getItem("tringAuthToken");
    const formDataImg = new FormData();
    formDataImg.append("avatar", file);

    try {
      const response = await fetch(API_ENDPOINTS.PARTNER_UPLOAD_AVATAR, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataImg,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload avatar");
      }

      const data = await response.json();
      console.log(data);
      setAvatarUrl(data.avatarUrl);
      toast.success("Profile photo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload profile photo");
      console.error("Image upload error:", error);
    }
  };

  // Enhanced validation with detailed messages
  const validateStep1 = () => {
    const missingFields = [];

    if (!formData.firstName) missingFields.push("First Name");
    if (!formData.lastName) missingFields.push("Last Name");
    if (!formData.gender) missingFields.push("Gender");

    if (!formData.dob.day || !formData.dob.month || !formData.dob.year) {
      missingFields.push("Date of Birth");
    }

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return false;
    }

    // Validate date of birth
    if (formData.dob.day && formData.dob.month && formData.dob.year) {
      const day = parseInt(formData.dob.day);
      const month = parseInt(formData.dob.month);
      const year = parseInt(formData.dob.year);

      // Basic date validation
      if (isNaN(day) || isNaN(month) || isNaN(year)) {
        toast.error("Invalid date values");
        return false;
      }

      if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900) {
        toast.error("Please enter a valid date");
        return false;
      }
    }

    return true;
  };

  const validateStep2 = () => {
    const missingFields = [];

    if (!formData.location) missingFields.push("City");
    if (!formData.email) missingFields.push("Email");
    if (!formData.stageName) missingFields.push("Stage Name");

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => setCurrentStep(1);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate both steps before submitting
    if (!validateStep1() || !validateStep2()) {
      return;
    }

    setIsSubmitting(true);

    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }

    const dobString = `${formData.dob.year}-${formData.dob.month
      .toString()
      .padStart(2, "0")}-${formData.dob.day.toString().padStart(2, "0")}`;

    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: localStorage.getItem("number") || "",
      gender: formData.gender,
      display_name: `${formData.profession} ${formData.stageName}`,
      dob: dobString,
      avatar_url: avatarUrl,
      location: formData.location,
      instagram: formData.instagram || "",
      twitter: formData.twitter || "",
      youtube: formData.youtube || "",
      linkedIn: formData.linkedIn || "",
      custom: formData.custom || "",
      role: formData.profession,
    };

    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PARTNER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to create partner");
      }

      const data = await res.json();
      console.log("signup", data);
      toast.success("Profile created successfully!");
      if (localStorage.getItem("isBusiness") == "true") {
        navigate("/loggedinartistbussiness");
      } else navigate("/loggedinartist");
    } catch (err) {
      toast.error(err.message || "Failed to create profile");
      console.error("Signup failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const Step1 = (
    <div className="md:space-y-4 space-y-2">
      <div className="text-center mb-4">
        <h3 className="text-3xl font-bold text-gray-800 flex items-center justify-center space-x-2">
          TRINGBO <img className="h-6" src="/images/logo2.svg" alt="Logo" />
        </h3>
        <p className="text-sm text-gray-600">
          Let's start with your basic details
        </p>
      </div>
      <div className="flex justify-center mb-4">
        <div className="flex justify-center">
          <div className="relative">
            <div className="h-24 w-24 rounded-full border-2 border-[#651FA8] overflow-hidden bg-gray-100">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-gray-400">
                  Photo
                </div>
              )}
            </div>
            <label
              htmlFor="photo-upload"
              className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-md cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-[#651FA8]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536M4 13.5V21h7.5L21 11.5l-7.5-7.5L4 13.5z"
                />
              </svg>
            </label>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-gray-700 mb-1 text-sm">First Name</label>
          <input
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter first name"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Last Name</label>
          <input
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter last name"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-gray-700 mb-2 text-sm">
          Date of Birth
        </label>
        <div className="grid grid-cols-3 gap-2">
          <select
            name="day"
            value={formData.dob.day}
            onChange={handleDOBChange}
            className="border rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          >
            <option value="">DD</option>
            {days.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            name="month"
            value={formData.dob.month}
            onChange={handleDOBChange}
            className="border rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          >
            <option value="">MM</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            name="year"
            value={formData.dob.year}
            onChange={handleDOBChange}
            className="border rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          >
            <option value="">YYYY</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-gray-700 mb-2 mt-2 text-sm">
            Gender
          </label>
          <div className="flex space-x-4">
            {["Male", "Female"].map((g) => (
              <label key={g} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="gender"
                  value={g}
                  checked={formData.gender === g}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-600"
                />
                <span className="text-gray-700 text-sm">{g}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const Step2 = (
    <div className="md:space-y-4 space-y-2">
      <div className="text-center mb-4">
        <h3 className="text-3xl font-bold text-gray-800 flex items-center justify-center space-x-2">
          TRINGBO <img className="h-6" src="/images/logo2.svg" alt="Logo" />
        </h3>
        <p className="text-sm text-gray-600">
          Complete your profile information
        </p>
      </div>

      <div>
        <label className="block text-gray-700 mb-2 text-sm">City</label>
        <input
          name="location"
          value={formData.location}
          onChange={handleChange}
          placeholder="Enter your city"
          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-gray-700 mb-2 text-sm">Profession</label>
        <div className="flex space-x-4">
          {["DJ", "KJ", "Singer", "Live Band"].map((opt) => (
            <label key={opt} className="flex items-center space-x-2">
              <input
                type="radio"
                name="profession"
                value={opt}
                checked={formData.profession === opt}
                onChange={handleChange}
                className="h-4 w-4 text-purple-600"
              />
              <span className="text-gray-700 text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1">
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Stage Name</label>
          <input
            name="stageName"
            value={formData.stageName}
            onChange={handleChange}
            placeholder="Enter stage name"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
          />
          <p className="mt-1 text-gray-500 text-xs">
            Visible as {formData.profession} {formData.stageName}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-[100dvh] w-screen bg-gray-50 flex overflow-hidden relative">
      <PremiumBackground />
      <ParticleBackground />
      <WaveAnimation />

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="w-full h-full flex items-center justify-center p-4 z-40">
        <div className="w-full max-w-lg bg-white border border-logo rounded-2xl shadow-xl md:p-5 p-4">
          <form>
            <div className="min-h-[350px]">
              <AnimatePresence mode="wait">
                {currentStep === 1 ? (
                  <motion.div
                    key="s1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    {Step1}
                  </motion.div>
                ) : (
                  <motion.div
                    key="s2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    {Step2}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="flex justify-between mt-6 gap-2">
              {currentStep === 2 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 border rounded-lg text-gray-700"
                >
                  Back
                </button>
              )}
              {currentStep === 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="ml-auto w-full px-6 py-2 bg-logo text-white rounded-lg"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-logo text-white rounded-lg w-full"
                >
                  {isSubmitting ? "Creating..." : "Create Profile"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
